import { HttpClient } from '@angular/common/http';
import { ErrorHandler, Injectable, NgZone, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Factory } from 'app/app.factory';
import { debounce } from 'lodash-es';
import { APP_VERSION } from 'version';

const MAX_EVENT_HISTORY = 15;
const DEBOUNCE_MS = 5000;
const CHUNK_RELOAD_COOLDOWN_MS = 10_000;
const CHUNK_RELOAD_KEY = 'motoboy_chunk_reload_at';
const FALLBACK_ROUTE = '/error-fallback';

const EXCLUDED_MESSAGES = [
    'location unavailable',
    'Location permission was denied',
    'ResizeObserver loop',
    'Non-Error promise rejection captured',
    'MetaMask',
    // MatIconRegistry: el set SVG no se pudo descargar (red/cancelación); el ícono existe y solo queda en blanco
    'Error retrieving icon',
];

const EXTENSION_STACK_PATTERNS = ['chrome-extension://', 'moz-extension://', 'safari-extension://', 'safari-web-extension://'];

const SENSITIVE_INPUT_PATTERNS = ['password', 'rut', 'dni', 'cuit', 'cpf', 'token', 'tarjeta', 'cvv', 'card', 'iban', 'cbu', 'cuenta'];

@Injectable()
export class GlobalErrorHandlerService implements ErrorHandler {
    private _http = inject(HttpClient);
    private _router = inject(Router);
    private _zone = inject(NgZone);
    private _factory = inject(Factory);

    private eventHistory: any[] = [];
    private lastNavigation: string | null = null;
    private logErrorDebounced: (error: any) => void;

    constructor() {
        this.logErrorDebounced = debounce(this.logErrorToServer.bind(this), DEBOUNCE_MS);

        if (typeof window === 'undefined') return;

        // Chrome corta el stack en 10 frames: quedaban solo frames de @angular/forms/core
        // y nunca el componente que origina el error.
        Error.stackTraceLimit = 50;

        this.trackUserEvents();

        window.addEventListener('unhandledrejection', (event) => {
            this.handleError(event.reason);
        });

        // Captura errores síncronos fuera de Angular zone (page.evaluate, scripts inyectados,
        // event handlers nativos sin ngZone wrap, etc.). Angular ErrorHandler solo captura
        // los que ocurren dentro de su zone — esto cubre el resto.
        window.addEventListener('error', (event) => {
            // Skip resource load failures (img/script/link fail to load):
            // event.target es el elemento HTML, event.error es null, event.message es ''.
            // Esos NO son errores de app — son recursos faltantes que generan spam genérico.
            if (event.target && event.target !== window) return;
            // Skip "Script error." sin detalles (CORS de scripts cross-origin sin crossorigin attr).
            if (!event.error && (!event.message || event.message === 'Script error.')) return;
            const err = event.error || new Error(event.message);
            this.handleError(err);
        });

        this._router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) this.recordNavigationEvent(event.urlAfterRedirects);
        });

        console.log('[error_handler] initialized', {
            env: this._factory?.env,
            debounceMs: DEBOUNCE_MS,
            cooldownMs: CHUNK_RELOAD_COOLDOWN_MS,
        });
    }

    handleError(error: any): void {
        try {
            if (!error) return;

            const errorMessage = String(error?.message || error?.payload?.message || error || '');

            if (this.isChunkLoadError(errorMessage)) {
                this.handleChunkLoadError();
                return;
            }

            if (EXCLUDED_MESSAGES.some((e) => errorMessage.includes(e))) return;
            if (this.isBrowserExtensionError(error)) return;

            console.error('[error_handler] captured', error);

            const errorDetails = {
                message: error?.payload?.message || error?.message || errorMessage || 'Unknown error',
                stack: this.truncate(error?.stack || '', 4000),
                url: error?.context?.url || error?.url || (typeof window !== 'undefined' ? window.location.href : ''),
                method: error?.context?.method || null,
                status: error?.status || null,
                context: error?.context || null,
                payload: error?.payload || null,
                app_version: APP_VERSION,
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
                timestamp: new Date().toISOString(),
                windowSize: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '',
                screenSize: typeof screen !== 'undefined' ? `${screen.width}x${screen.height}` : '',
                scrollPosition: typeof window !== 'undefined' ? window.scrollY : 0,
                eventHistory: this.eventHistory.slice(-MAX_EVENT_HISTORY),
            };

            this.logErrorDebounced(errorDetails);
        } catch (selfErr) {
            console.error('[error_handler] self-error (suppressed)', selfErr);
        }
    }

    /** Errores lanzados por extensiones del navegador (MetaMask, etc.) — no son de la app */
    private isBrowserExtensionError(error: any): boolean {
        const stack = String(error?.stack || '');
        return EXTENSION_STACK_PATTERNS.some((p) => stack.includes(p));
    }

    private logErrorToServer(errorDetails: any): void {
        try {
            if (!this._factory) return;
            if (this._factory.env === 'local') return;

            const accessToken = typeof localStorage !== 'undefined' ? localStorage.getItem('accessToken') : null;
            if (!accessToken) return;

            const url = `${this._factory.coruscant}/admin/errors`;

            this._http.post(url, errorDetails).subscribe({
                next: () => {},
                error: (err) => {
                    if (err?.status === 401 || err?.status === 403) return;
                    if (err?.status === 0 || err?.status >= 500) return;
                    console.error('[error_handler] failed to log error', err?.status);
                },
            });
        } catch (e) {
            console.error('[error_handler] logErrorToServer threw', e);
        }
    }

    private trackUserEvents(): void {
        const eventsToTrack = ['click', 'input', 'change'];

        eventsToTrack.forEach((eventType) => {
            document.addEventListener(
                eventType,
                (event: Event) => {
                    try {
                        const target = event.target as HTMLElement;
                        if (!target) return;

                        const isSensitive = this.isSensitiveInput(target);
                        const text = target.textContent?.trim().slice(0, 50) || null;
                        const value = (target as HTMLInputElement)?.value;

                        const eventData: any = {
                            event: eventType,
                            element: target.tagName?.toLowerCase() || 'unknown',
                            id: target.id || null,
                            classes: (typeof target.className === 'string' ? target.className : target.classList?.toString()) || null,
                            text: isSensitive ? '[REDACTED]' : text,
                            value_length: isSensitive && typeof value === 'string' ? value.length : undefined,
                            location: window.location.pathname,
                            timestamp: Date.now(),
                        };

                        if (eventType === 'click') {
                            const mouseEvent = event as MouseEvent;
                            eventData.x = mouseEvent.clientX;
                            eventData.y = mouseEvent.clientY;
                        }

                        this.recordEvent(eventData);
                    } catch {
                        // tracking best-effort: nunca debe romper el evento del usuario
                    }
                },
                true
            );
        });
    }

    private isSensitiveInput(target: HTMLElement): boolean {
        if (!(target instanceof HTMLInputElement)) return false;
        if (target.type === 'password') return true;

        const auto = target.autocomplete?.toLowerCase() || '';
        if (auto.includes('password') || auto.includes('cc-number')) return true;

        const name = target.name?.toLowerCase() || '';
        const id = target.id?.toLowerCase() || '';
        const placeholder = target.placeholder?.toLowerCase() || '';

        return SENSITIVE_INPUT_PATTERNS.some((p) => name.includes(p) || id.includes(p) || placeholder.includes(p));
    }

    private recordNavigationEvent(url: string): void {
        if (this.lastNavigation === url) return;
        this.lastNavigation = url;
        this.recordEvent({ event: 'navigation', location: url, timestamp: Date.now() });
    }

    private recordEvent(eventData: any): void {
        if (this.eventHistory.length >= MAX_EVENT_HISTORY) this.eventHistory.shift();
        this.eventHistory.push(eventData);
    }

    private isChunkLoadError(message: string): boolean {
        if (!message) return false;
        const patterns = ['Failed to fetch dynamically imported module', 'Loading chunk', 'ChunkLoadError', 'Loading CSS chunk'];
        return patterns.some((p) => message.includes(p));
    }

    private handleChunkLoadError(): void {
        if (typeof sessionStorage === 'undefined' || typeof window === 'undefined') return;

        const lastReload = sessionStorage.getItem(CHUNK_RELOAD_KEY);
        const now = Date.now();

        if (!lastReload || now - parseInt(lastReload, 10) > CHUNK_RELOAD_COOLDOWN_MS) {
            sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
            console.warn('[error_handler] chunk load — reloading');
            this._zone.runOutsideAngular(() => window.location.reload());
            return;
        }

        console.warn('[error_handler] chunk load — second attempt blocked, redirecting to fallback');
        this._zone.run(() => this._router.navigate([FALLBACK_ROUTE]));
    }

    private truncate(s: string, max: number): string {
        if (!s) return '';
        return s.length <= max ? s : s.slice(0, max) + ' ... [truncated]';
    }
}
